import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Renders its children into a separate browser window (window.open) via a React
// portal, so the detached panel stays part of the same React tree — state and
// handlers stay fully live across windows with no syncing. Used to move a tool
// panel onto a second monitor.
//
// The child window inherits the app's styles: we clone every <style> and
// stylesheet <link> from the main document into the popout's head once, then keep
// them in sync (dev/HMR injects new <style> nodes) with a MutationObserver.
export default function PopoutWindow({ title = 'Odinflow', width = 340, height = 800, onClose, children }) {
  const [container, setContainer] = useState(null);
  const winRef = useRef(null);

  useEffect(() => {
    const left = Math.max(0, Math.round((window.screen.availWidth || 1200) / 2 - width / 2));
    const top = 120;
    const features = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`;
    const win = window.open('', '', features);
    if (!win) {
      // Popup blocked — tell the user and re-dock so the tool doesn't vanish.
      // eslint-disable-next-line no-alert
      window.alert('Your browser blocked the pop-out window.\n\nAllow pop-ups for this site, then try “Pop out” again.');
      onClose && onClose();
      return;
    }
    win.focus();
    winRef.current = win;

    win.document.title = title;
    win.document.body.style.margin = '0';
    win.document.body.style.background = '#F6F1E9';
    win.document.body.style.fontFamily = 'Nunito Sans, Avenir, sans-serif';

    // Copy existing styles/fonts, then observe for any added later (HMR, lazy CSS).
    const copied = new WeakSet();
    const copyNode = (node) => {
      if (copied.has(node)) return;
      copied.add(node);
      try { win.document.head.appendChild(node.cloneNode(true)); } catch (e) { /* ignore */ }
    };
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach(copyNode);
    const observer = new MutationObserver(muts => {
      muts.forEach(m => m.addedNodes.forEach(n => {
        if (n.nodeName === 'STYLE' || (n.nodeName === 'LINK' && n.rel === 'stylesheet')) copyNode(n);
      }));
    });
    observer.observe(document.head, { childList: true });

    const mount = win.document.createElement('div');
    mount.style.height = '100%';
    win.document.body.appendChild(mount);
    setContainer(mount);

    // Closing the popup (or the main window) re-docks the panel.
    const handleClose = () => { onClose && onClose(); };
    win.addEventListener('beforeunload', handleClose);
    window.addEventListener('beforeunload', () => { try { win.close(); } catch (e) {} });

    return () => {
      observer.disconnect();
      win.removeEventListener('beforeunload', handleClose);
      try { win.close(); } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return container ? createPortal(children, container) : null;
}
